/* eslint no-bitwise: 0 */
import { RootState } from 'gf-types';
import { hasBinaryPermission, getFeature, isPublicForm } from './PermissionUtility';
import { AppFeatures, Features, Operations } from './PermissionUtility-types';
import { isSpecialAccessLogin } from './UserUtility';
import { SubscriptionCodes } from './constants';

const getFeatureValueOnly = (feature: Features) => getFeature(feature, false);

const AppFeatureCanIUse: { [key: string]: (state: RootState) => boolean } = {
  [AppFeatures.AddOns]: (state) => state.profile.isAdmin && getFeatureValueOnly(Features.AddOns),
  [AppFeatures.ApiCredentialsUI]: (state) =>
    state.profile.isAdmin &&
    (getFeatureValueOnly(Features.ApiReadAccess) ||
      getFeatureValueOnly(Features.ApiWriteAccess) ||
      getFeatureValueOnly(Features.ApiDatasourcesReadAccess) ||
      getFeatureValueOnly(Features.ApiDatasourcesWriteAccess)),
  [AppFeatures.ConnectedApps]: (state) =>
    ((state.profile.isAdmin || hasBinaryPermission(Operations.ManageWorkflows)) && getFeatureValueOnly(Features.ConnectedApps)) || state.profile.isInTrial,
  [AppFeatures.CreateGroups]: (state) => state.profile.client.subscription !== SubscriptionCodes.Teacher,
  [AppFeatures.CreateUsers]: (state) => state.profile.client.subscription !== SubscriptionCodes.Teacher,
  [AppFeatures.Dashboard]: (state) => state.profile.isAdmin,
  [AppFeatures.PremiumPerDataSource]: (_state) => getFeatureValueOnly(Features.PremiumDataSources) && getFeatureValueOnly(Features.PremiumPerDataSource),
  [AppFeatures.eSignature]: (_state) => isPublicForm() || getFeatureValueOnly(Features.eSignature),
  [AppFeatures.FormCopy]: (_state) => hasBinaryPermission(Operations.FormCopy),
  [AppFeatures.FormComplete]: (_state) => hasBinaryPermission(Operations.FormComplete),
  [AppFeatures.FormDelete]: (_state) => hasBinaryPermission(Operations.FormDelete),
  [AppFeatures.FormDeleteDrafts]: (_state) => hasBinaryPermission(Operations.FormDeleteDrafts),
  [AppFeatures.FormList]: (state) =>
    state.profile.client.subscription !== SubscriptionCodes.Basic ||
    (state.profile.client.subscription === SubscriptionCodes.Basic && state.profile.isInTrial) ||
    getFeatureValueOnly(Features.PublicFormsPilot),
  [AppFeatures.FormPDF]: (_state) => hasBinaryPermission(Operations.FormDownloadPDF),
  [AppFeatures.FormRename]: (_state) => hasBinaryPermission(Operations.FormRename),
  [AppFeatures.FormReopen]: (_state) => hasBinaryPermission(Operations.FormEdit),
  [AppFeatures.FormRevert]: (state) => state.profile.isAdmin && getFeatureValueOnly(Features.FormRevert),
  [AppFeatures.FormTransfer]: (_state) => hasBinaryPermission(Operations.FormTransfer),
  [AppFeatures.HIPAASetup]: (state) => state.profile.isAdmin && getFeatureValueOnly(Features.HIPAASetup),
  [AppFeatures.LegacyPricing]: (state) => state.profile.client.legacyPricing,
  [AppFeatures.ManageAutoEmail]: (state) =>
    state.profile.isAdmin && !getFeatureValueOnly(Features.DisableAutoEmail) && getFeatureValueOnly(Features.TemplateEvents),
  [AppFeatures.ManageDataSources]: (state) => state.profile.isAdmin || hasBinaryPermission(Operations.ManageDataSources),
  [AppFeatures.ManageSettings]: (state) => state.profile.isAdmin,
  [AppFeatures.ManageSubscription]: (state) =>
    state.profile.isAdmin && !state.profile.client.isManualInvoice && getFeatureValueOnly(Features.ManageSubscription),
  [AppFeatures.ManageUsers]: (_state) => hasBinaryPermission(Operations.ManageUsersGroups),
  [AppFeatures.ManageUsersAdmin]: (state) => state.profile.isAdmin,
  [AppFeatures.MessageStatus]: (state) => state.profile.isAdmin,
  [AppFeatures.PreviewFeatures]: (state) => state.profile.isAdmin && getFeatureValueOnly(Features.PreviewFeatures),
  [AppFeatures.ProfileNotifications]: (state) => state.profile.isAdmin,
  [AppFeatures.PublicShare]: (state) =>
    hasBinaryPermission(Operations.PublicShare) &&
    (state.profile.isInTrial || getFeatureValueOnly(Features.PublicShare) || getFeatureValueOnly(Features.PublicFormsPilot)),
  [AppFeatures.PublicShareBuyCredits]: (state) =>
    state.profile.isAdmin &&
    !state.profile.client.isManualInvoice &&
    getFeatureValueOnly(Features.ManageSubscription) &&
    !getFeatureValueOnly(Features.PublicFormsPilot),
  [AppFeatures.PublicFormCreditLimits]: (state) =>
    getFeatureValueOnly(Features.UsePublicFormCreditLimits) && !state.profile.client.isManualInvoice && !state.profile.isInTrial,
  [AppFeatures.PublicFormCreditLimitsTrial]: (state) => getFeatureValueOnly(Features.UsePublicFormCreditLimits) && state.profile.isInTrial,
  [AppFeatures.Reports]: (state) => hasBinaryPermission(Operations.ViewReports) && (state.profile.isInTrial || getFeatureValueOnly(Features.ReportAccess)),
  [AppFeatures.RetainImageAsAttachment]: (_state) => getFeatureValueOnly(Features.FileManagement) && getFeatureValueOnly(Features.RetainImageAsAttachment),
  [AppFeatures.TagAdd]: () => hasBinaryPermission(Operations.FormTags),
  [AppFeatures.TagCreate]: () => hasBinaryPermission(Operations.TagCreate),
  [AppFeatures.TagManage]: () => hasBinaryPermission(Operations.TagManage),
  [AppFeatures.TagModal]: () =>
    hasBinaryPermission(Operations.FormTags) || hasBinaryPermission(Operations.TagCreate) || hasBinaryPermission(Operations.TagManage),
  [AppFeatures.TakeOwnership]: (_state) => !getFeatureValueOnly(Features.DisableTakeOwnership) && hasBinaryPermission(Operations.FormTransfer),
  [AppFeatures.TemplateEditorJson]: (_state) => isSpecialAccessLogin(),
  [AppFeatures.TemplateList]: (_state) => hasBinaryPermission(Operations.ViewTemplates),
  [AppFeatures.TemplateFolders]: (state) => state.profile.isAdmin,
  [AppFeatures.WebhooksUI]: (state) => getFeatureValueOnly(Features.ApiReadAccess) && state.profile.isAdmin,
  [AppFeatures.Workflow]: (_state) => hasBinaryPermission(Operations.ManageWorkflows) && (getFeatureValueOnly(Features.Workflow) || _state.profile.isInTrial),
  [AppFeatures.WorkflowJson]: (_state) => isSpecialAccessLogin() && hasBinaryPermission(Operations.ManageWorkflows),
  [AppFeatures.GoPilot]: (_state) => isSpecialAccessLogin() && getFeatureValueOnly(Features.GoPilot)
};

export default AppFeatureCanIUse;
